import { IAASHTOResults, IPavedData } from '../../api/IApiResponse';
import { UNIT_CONVERSION, DEFAULT_LCR } from '../../constants/AASHTOConstants';

export interface DesignInputs {
    surfaceThickness?: number;
    baseThickness?: number;
    subbaseThickness?: number;
}

export interface TableDataResult {
    passesFirstYear?: number;
    w18?: number;
    newW18?: number;
    thickness?: number;
    tbr?: number;
    thicknessSaving?: number;
}

export interface DesignTableData {
    tableData1?: TableDataResult; // Unstabilized
    tableData2?: TableDataResult; // Increased passages
    tableData3?: TableDataResult; // Stabilized
}

export interface AllDesignTableData {
    surfaceDesignTableData?: DesignTableData;
    baseDesignTableData?: DesignTableData;
    subbaseDesignTableData?: DesignTableData;
}

export class PavedCalculationService {
    constructor(
        private aashtoResults: IAASHTOResults | null,
        private projectData: any
    ) {}

    /**
     * Calculates thickness for a given scenario and configuration
     */
    public recalculateThickness(scenario: 'surface' | 'base' | 'subbase', inputs: DesignInputs, stabilised = false): number {
        if (!this.aashtoResults?.detailedCalculations) return 0;

        // Use the Goal Seek calculated totalSN instead of Excel cell calculator SN
        const structuralNumberSN = this.aashtoResults.totalSN;
        const { surface, base, subbase } = this.aashtoResults.detailedCalculations.layerCoefficients;

        const totalSN = structuralNumberSN;

        // Layer coefficients
        const a1 = surface.a1;
        const a2 = base.a2;
        const a3 = subbase.a3;

        // CRITICAL FIX: Excel drainage coefficient logic (CALCULATION.txt G7, G8, H7, H8)
        // - Unstabilised (G columns): Use DYNAMIC values from Design_LCR!K12, K25
        //   K12 = DRAINAGE_m!D27 (VLOOKUP based on quality + moisture)
        //   K25 = DRAINAGE_m!D52 (VLOOKUP based on quality + moisture)
        // - Stabilised (H columns): use CONDITIONAL values via IF formulas
        //   H7 = IF('3_Drainage_Design'!C4="No", G7, Design_LCR!K16)
        //   H8 = IF('3_Drainage_Design'!C6="No", G8, Design_LCR!K29)
        // Force recompilation timestamp: 2025-01-27

        // DYNAMIC values calculated by DrainageCoefficientService via PavedCalculationOrchestrator
        // and passed through CalculationServiceFacade.layerCoefficients
        const m2_unstabilised_dynamic = base.m2; // Excel G7 = Design_LCR!K12 = DRAINAGE_m!D27 (e.g., 0.8)
        const m3_unstabilised_dynamic = subbase.m3; // Excel G8 = Design_LCR!K25 = DRAINAGE_m!D52 (e.g., 0.8)
        const m2_stabilised_dynamic = base.m2_stabilised || base.m2; // Excel H7 (conditional GCD)
        const m3_stabilised_dynamic = subbase.m3_stabilised || subbase.m3; // Excel H8 (conditional GCD)

        // BUGFIX: Remove hardcoded fallbacks that override DrainageCoefficientService calculations
        // DrainageCoefficientService calculates m2/m3 from the time to drain with the drainage product geometry
        // Previous hardcoded values (m2_stabilised_gcd=1.0, m3_stabilised_gcd=1.15) were based on outdated quality assumptions
        // and caused custom design to calculate wrong thickness (0.52m instead of 0.46m for subbase)
        // Reason: the dynamic DrainageCoefficientService coefficients made these hardcoded values obsolete

        // Determine the correct m-value based on stabilisation
        const m2 = stabilised ? m2_stabilised_dynamic : m2_unstabilised_dynamic;
        const m3 = stabilised ? m3_stabilised_dynamic : m3_unstabilised_dynamic;

        // LCR values for stabilised calculations
        // CRITICAL FIX: Fallback values were INVERTED! Excel CALCULATION.txt C8/C9:
        // - C8 (lcrBase) = Design_LCR!G85 = 1.54147 (MacGrid EG 20S for base)
        // - C9 (lcrSubbase) = Design_LCR!K85 = 1.588617 (MacGrid EG 30S for subbase with CBR=10)
        const lcrBase = base.lcr || DEFAULT_LCR.BASE_EG20S; // Excel C8 (was incorrectly 1.588617)
        const lcrSubbase = subbase.lcr || DEFAULT_LCR.SUBBASE_EG30S; // Excel C9 (was incorrectly 1.67984)

        let calculatedThicknessMeters = 0;

        if (scenario === 'surface') {
            const baseThickness_in = (inputs.baseThickness ?? 0) / UNIT_CONVERSION.METERS_TO_INCHES;
            const subbaseThickness_in = (inputs.subbaseThickness ?? 0) / UNIT_CONVERSION.METERS_TO_INCHES;
            const baseSN = a2 * baseThickness_in * m2 * (stabilised ? lcrBase : 1);
            const subbaseSN = a3 * subbaseThickness_in * m3 * (stabilised ? lcrSubbase : 1);
            const remainingSN = totalSN - baseSN - subbaseSN;
            const surfaceThickness_in = remainingSN / a1;
            calculatedThicknessMeters = surfaceThickness_in * UNIT_CONVERSION.INCHES_TO_METERS;
        } else if (scenario === 'base') {
            const surfaceThickness_in = (inputs.surfaceThickness ?? 0) / UNIT_CONVERSION.METERS_TO_INCHES;
            const subbaseThickness_in = (inputs.subbaseThickness ?? 0) / UNIT_CONVERSION.METERS_TO_INCHES;
            const surfaceSN = a1 * surfaceThickness_in;
            const subbaseSN = a3 * subbaseThickness_in * m3 * (stabilised ? lcrSubbase : 1);
            const remainingSN = totalSN - surfaceSN - subbaseSN;
            const divisor = a2 * m2 * (stabilised ? lcrBase : 1);
            const baseThickness_in = divisor > 0 ? remainingSN / divisor : 0;
            calculatedThicknessMeters = baseThickness_in * UNIT_CONVERSION.INCHES_TO_METERS;
        } else if (scenario === 'subbase') {
            const surfaceThickness_in = (inputs.surfaceThickness ?? 0) / UNIT_CONVERSION.METERS_TO_INCHES;
            const baseThickness_in = (inputs.baseThickness ?? 0) / UNIT_CONVERSION.METERS_TO_INCHES;
            const surfaceSN = a1 * surfaceThickness_in;
            const baseSN = a2 * baseThickness_in * m2 * (stabilised ? lcrBase : 1);
            const remainingSN = totalSN - surfaceSN - baseSN;
            const divisor = a3 * m3 * (stabilised ? lcrSubbase : 1);
            const subbaseThickness_in = divisor > 0 ? remainingSN / divisor : 0;
            calculatedThicknessMeters = subbaseThickness_in * UNIT_CONVERSION.INCHES_TO_METERS;
        }

        return Math.max(0, Math.ceil(calculatedThicknessMeters * 100) / 100);
    }

    /**
     * Calculates dynamic passes for a given scenario and configuration
     */
    public calculateDynamicPasses(scenario: 'surface' | 'base' | 'subbase', inputs: DesignInputs, stabilised = false): number {
        if (!this.aashtoResults?.detailedCalculations) return 0;

        let totalThicknessCurrentScenario = 0;
        let totalThicknessOriginal = 0;

        if (scenario === 'surface') {
            const surfaceThickness = this.recalculateThickness('surface', inputs, stabilised);
            totalThicknessCurrentScenario = surfaceThickness + (inputs.baseThickness ?? 0) + (inputs.subbaseThickness ?? 0);

            const softwareDesign = this.aashtoResults.detailedCalculations.softwareDesign;
            if (softwareDesign) {
                totalThicknessOriginal = softwareDesign.surface.unstabilised + softwareDesign.base.unstabilised + softwareDesign.subbase.unstabilised;
            }
        } else if (scenario === 'base') {
            const baseThickness = this.recalculateThickness('base', inputs, stabilised);
            totalThicknessCurrentScenario = (inputs.surfaceThickness ?? 0) + baseThickness + (inputs.subbaseThickness ?? 0);

            const softwareDesign = this.aashtoResults.detailedCalculations.softwareDesign;
            if (softwareDesign) {
                totalThicknessOriginal = softwareDesign.surface.unstabilised + softwareDesign.base.unstabilised + softwareDesign.subbase.unstabilised;
            }
        } else if (scenario === 'subbase') {
            const subbaseThickness = this.recalculateThickness('subbase', inputs, stabilised);
            totalThicknessCurrentScenario = (inputs.surfaceThickness ?? 0) + (inputs.baseThickness ?? 0) + subbaseThickness;

            const softwareDesign = this.aashtoResults.detailedCalculations.softwareDesign;
            if (softwareDesign) {
                totalThicknessOriginal = softwareDesign.surface.unstabilised + softwareDesign.base.unstabilised + softwareDesign.subbase.unstabilised;
            }
        }

        const basePassesFirstYear = this.aashtoResults.detailedCalculations.esalFactors.esalUnstabilised;
        const thicknessRatio = totalThicknessOriginal > 0 ? totalThicknessCurrentScenario / totalThicknessOriginal : 1;

        let dynamicPasses = basePassesFirstYear * thicknessRatio;

        if (stabilised) {
            const tbr = this.aashtoResults.detailedCalculations.esalFactors.tbr;
            dynamicPasses *= tbr;
        }

        return Math.round(dynamicPasses);
    }

    /**
     * Calculates dynamic W18 based on dynamic passes
     */
    public calculateDynamicW18(dynamicPasses: number): number {
        if (!this.aashtoResults?.detailedCalculations) return 0;

        const originalPasses = this.aashtoResults.detailedCalculations.esalFactors.esalUnstabilised;
        const originalW18 = this.aashtoResults.detailedCalculations.esalFactors.logW18Unstabilised;

        if (originalPasses === 0) return originalW18;

        const passesRatio = dynamicPasses / originalPasses;
        const dynamicW18 = originalW18 + Math.log10(passesRatio);

        return dynamicW18;
    }

    /**
     * Calculates table data for a specific scenario (surface, base, subbase)
     */
    public calculateDesignTableData(scenario: 'surface' | 'base' | 'subbase', inputs: DesignInputs): DesignTableData {
        // Calculate unstabilized data (tableData1)
        const unstabilizedPasses = this.calculateDynamicPasses(scenario, inputs, false);
        const unstabilizedW18 = this.calculateDynamicW18(unstabilizedPasses);
        const unstabilizedThickness = this.recalculateThickness(scenario, inputs, false);

        // Calculate stabilized data for tableData2 (increased passages)
        const stabilizedPasses = this.calculateDynamicPasses(scenario, inputs, true);
        const stabilizedW18 = this.calculateDynamicW18(stabilizedPasses);
        const tbr = this.aashtoResults?.detailedCalculations?.esalFactors?.tbr || 1;

        // Calculate stabilized thickness
        const stabilizedThickness = this.recalculateThickness(scenario, inputs, true);

        // Calculate thickness saving percentage (stabilized vs unstabilized thickness)
        const thicknessSaving = unstabilizedThickness > 0 ? ((unstabilizedThickness - stabilizedThickness) / unstabilizedThickness) * 100 : 0;

        return {
            tableData1: {
                passesFirstYear: unstabilizedPasses,
                w18: parseFloat(unstabilizedW18.toFixed(3)),
                thickness: parseFloat(unstabilizedThickness.toFixed(3)),
            },
            tableData2: {
                passesFirstYear: stabilizedPasses,
                newW18: parseFloat(stabilizedW18.toFixed(3)),
                tbr: parseFloat(tbr.toFixed(3)),
            },
            tableData3: {
                // tableData3 should use unstabilized passes and W18 (same traffic as original design)
                // but show thickness saving from using stabilized design
                passesFirstYear: unstabilizedPasses,
                w18: parseFloat(unstabilizedW18.toFixed(3)),
                thicknessSaving: parseFloat(Math.max(0, thicknessSaving).toFixed(1)),
            },
        };
    }

    /**
     * Calculates all table data for surface, base, and subbase scenarios
     */
    public calculateAllDesignTableData(surfaceInputs: DesignInputs, baseInputs: DesignInputs, subbaseInputs: DesignInputs): AllDesignTableData {
        return {
            surfaceDesignTableData: this.calculateDesignTableData('surface', surfaceInputs),
            baseDesignTableData: this.calculateDesignTableData('base', baseInputs),
            subbaseDesignTableData: this.calculateDesignTableData('subbase', subbaseInputs),
        };
    }
}
